// SPDX-License-Identifier: GPL-3.0-or-later
//
// Adjust user templates to the current profile. The AI looks at the original
// template + the profile rawText and returns a trimmed template that drops
// placeholders, sections, and layout the profile can't ground. The originals
// stay on disk untouched — every analysis starts from them — so when the
// profile grows, previously-trimmed sections naturally come back.
//
// Runs as a fire-and-forget after profile save (rawText change only) and from
// the Settings "Re-analyze" button. Skipped when AI is off.

import { complete } from '../ai/client';
import { stripFences } from '../ai/parse';
import type { DocKind } from '../generate';
import { renderTemplate } from '../typst/render';
import { getWalletState } from '../wallet';
import {
  ensureOriginalTemplate,
  writeActiveTemplate,
  writeTemplateMeta,
  type TemplateGap,
  type TemplateMeta,
} from './templates';

const KINDS: readonly DocKind[] = ['cv', 'cover', 'interview'] as const;

export interface AnalysisOutcome {
  kind: DocKind;
  ok: boolean;
  gaps?: TemplateGap[];
  message?: string;
}

const SYSTEM_PROMPT = `You receive a Typst document template with Mustache placeholders and a user profile.
Your job is to produce an *adjusted* template that drops every placeholder, section, or surrounding layout element the profile cannot ground — and keeps everything else exactly as written (same fonts, spacing, fixed text, section order).

Mustache syntax in the template:
- {{var}} or {{{var}}} — variable substitution. Drop if the profile has no information to fill it.
- {{#section}}…{{/section}} — iterable / truthy block. Drop the entire open + body + close if the profile cannot fill it.
- {{^section}}…{{/section}} — inverse block. Keep unless its inner content references unfillable paths.
- Dot-paths like {{contact.email}} are supported.

You output ONLY valid JSON of this exact shape:
{
  "adjustedTemplate": string,    // full Typst source of the adjusted template
  "gaps": [{
    "path": string,              // e.g. "applicant.phone" or "education"
    "scope": "section" | "field",
    "reason": string             // one short sentence: what the profile lacks
  }],
  "notes"?: string               // optional one-liner shown to the user
}

Hard rules:
- No markdown, no code fences, no commentary outside the JSON object.
- Be conservative: if you're not sure whether the profile supports a placeholder, KEEP it. Only trim when the profile clearly lacks the information.
- Never invent profile content.
- Do not restyle, reword fixed text, or reorder sections. Only remove.
- Keep Mustache syntax balanced: every {{#x}} you keep must have its matching {{/x}}.
- If the profile supports the whole template, return it unchanged with gaps: [].
- Layout elements (e.g. a #v(...) spacer or a heading) that only existed to introduce a dropped section should be dropped with it.`;

function userPrompt(kind: DocKind, template: string, profileText: string): string {
  return `<doc-kind>${kind}</doc-kind>

<profile>
${profileText.trim() || '(profile is empty)'}
</profile>

<template>
${template}
</template>

Produce the adjusted template and gap list as JSON. Output only the JSON.`;
}

interface ParsedAdjustment {
  adjustedTemplate: string;
  gaps: TemplateGap[];
  notes?: string;
}

function parseAdjustment(raw: string): ParsedAdjustment | { error: string } {
  const text = stripFences(raw);
  let value: unknown;
  try {
    value = JSON.parse(text);
  } catch (err) {
    return { error: `AI output was not valid JSON: ${err instanceof Error ? err.message : String(err)}` };
  }
  if (!value || typeof value !== 'object') return { error: 'AI output was not a JSON object.' };
  const o = value as Record<string, unknown>;
  if (typeof o['adjustedTemplate'] !== 'string') {
    return { error: 'AI output missing "adjustedTemplate" (string).' };
  }
  if (!Array.isArray(o['gaps'])) {
    return { error: 'AI output missing "gaps" (array).' };
  }
  const gaps: TemplateGap[] = [];
  for (const g of o['gaps']) {
    if (!g || typeof g !== 'object') continue;
    const gg = g as Record<string, unknown>;
    if (typeof gg['path'] !== 'string' || typeof gg['reason'] !== 'string') continue;
    const scope = gg['scope'] === 'section' ? 'section' : 'field';
    gaps.push({ path: gg['path'], scope, reason: gg['reason'] });
  }
  const out: ParsedAdjustment = {
    adjustedTemplate: o['adjustedTemplate'] as string,
    gaps,
  };
  if (typeof o['notes'] === 'string' && o['notes']) out.notes = o['notes'];
  return out;
}

// Walk the template with empty data — the renderer throws on unbalanced
// {{#x}}/{{/x}} sections, which is the most common way an AI rewrite breaks.
// We can't catch Typst-syntax errors without compiling; for those the user
// will see the generation error and can re-run analysis.
function validateMustacheBalance(template: string): { ok: true } | { ok: false; message: string } {
  try {
    renderTemplate(template, {});
    return { ok: true };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

export interface AnalyzeOptions {
  profileText: string;
  profileUpdatedAt?: string;
}

export async function analyzeTemplate(
  root: FileSystemDirectoryHandle,
  kind: DocKind,
  opts: AnalyzeOptions,
): Promise<AnalysisOutcome> {
  if (!getWalletState().connection) {
    return { kind, ok: false, message: 'AI is off — connect the wallet to analyze templates.' };
  }
  const { source, text: original } = await ensureOriginalTemplate(root, kind);

  let raw: string;
  try {
    const res = await complete({
      kind: 'quick',
      system: SYSTEM_PROMPT,
      user: userPrompt(kind, original, opts.profileText),
      maxTokens: 4096,
    });
    raw = res.content;
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    await writeTemplateMeta(root, kind, {
      schemaVersion: 1,
      source,
      gaps: [],
      analyzedAt: new Date().toISOString(),
      ...(opts.profileUpdatedAt !== undefined ? { profileUpdatedAt: opts.profileUpdatedAt } : {}),
      error: `AI call failed: ${message}`,
    });
    return { kind, ok: false, message };
  }

  const parsed = parseAdjustment(raw);
  if ('error' in parsed) {
    await writeTemplateMeta(root, kind, {
      schemaVersion: 1,
      source,
      gaps: [],
      analyzedAt: new Date().toISOString(),
      ...(opts.profileUpdatedAt !== undefined ? { profileUpdatedAt: opts.profileUpdatedAt } : {}),
      error: parsed.error,
    });
    return { kind, ok: false, message: parsed.error };
  }

  const balance = validateMustacheBalance(parsed.adjustedTemplate);
  if (!balance.ok) {
    await writeTemplateMeta(root, kind, {
      schemaVersion: 1,
      source,
      gaps: [],
      analyzedAt: new Date().toISOString(),
      ...(opts.profileUpdatedAt !== undefined ? { profileUpdatedAt: opts.profileUpdatedAt } : {}),
      error: `Adjusted template is structurally broken: ${balance.message}`,
    });
    return { kind, ok: false, message: balance.message };
  }

  await writeActiveTemplate(root, kind, parsed.adjustedTemplate);
  const meta: TemplateMeta = {
    schemaVersion: 1,
    source,
    gaps: parsed.gaps,
    analyzedAt: new Date().toISOString(),
    ...(opts.profileUpdatedAt !== undefined ? { profileUpdatedAt: opts.profileUpdatedAt } : {}),
    ...(parsed.notes !== undefined ? { notes: parsed.notes } : {}),
  };
  await writeTemplateMeta(root, kind, meta);
  return { kind, ok: true, gaps: parsed.gaps };
}

export async function analyzeAllTemplates(
  root: FileSystemDirectoryHandle,
  opts: AnalyzeOptions,
): Promise<AnalysisOutcome[]> {
  // Sequential to keep wallet/provider rate-limits gentle — these are cheap
  // quick-model calls so total latency is fine; parallel risks a 429 if the
  // user's provider key is tight.
  const out: AnalysisOutcome[] = [];
  for (const kind of KINDS) {
    out.push(await analyzeTemplate(root, kind, opts));
  }
  return out;
}
